import type { BatchRegisterRow, DispatchLine, SiteOrder } from '@api/vendor-portal';
import { today as localToday } from '@utils/productionWindow';

import { batchQcSplit, isBatchExpired } from './batchQcSplit';

import { resolveWorkOrder, type OrderLine } from './batchGridRows';
import { formatCount } from '@utils/currency';

/**
 * One line of a shipment, as the builder holds it while somebody edits.
 *
 * KEYED BY LINE, NOT BY BATCH. A batch may appear on several lines — 350 against the order that
 * wants them and 50 as stock — so the batch no longer identifies a row.
 *
 * The quantity is a string because it is a text input mid-typing: "" and "4" are states a person
 * passes through on the way to "400", and coercing to a number would turn the first into 0 and
 * silently accept it.
 */
export interface ShipmentRow {
    lineId: string;
    /** The batch, or the register's synthetic key for a `noBatch` row. */
    batchId: string;
    code: string;
    /** Drawing on the no-batch bucket: sent as a product line, labelled "No batch". */
    noBatch?: boolean;
    itemId: string;
    itemName: string;
    /** What the BATCH has left. Shared by every line drawing on it, never divided between them. */
    available: number;
    quantity: string;
    /** The picker's own value: '' for stock, `po:<id>`, or `wo:<id>`. */
    orderChoice: string;
    /**
     * The order this batch was MADE against, and what to call it — fixed for the row's life,
     * where `orderChoice` is whatever is currently picked.
     *
     * Held separately because the two answer different questions, and the picker needs both: it
     * offers this one and stock, and nothing else. A split line inherits it, so the leftover of
     * a run can be put back on the order it came from.
     */
    ownChoice: string;
    ownLabel: string | null;
    /**
     * Whether `ownChoice` is the batch's real provenance link, as opposed to FIFO's
     * suggestion for a free batch. Provenance restricts the picker to that order and stock;
     * a suggestion is a default among every open order asking for the SKU.
     */
    hasProvenance?: boolean;
    /**
     * When the batch goes off, for the line that says so beside its code. Null for undated
     * batches and for the no-batch bucket, which the dispatch order already lists last.
     */
    expiryDate?: string | null;
}

/**
 * Whether a batch can go on a shipment.
 *
 * NOT RULED AGAINST, AND STILL HERE. An unruled batch may go: the brand inspects the load at
 * its own dock and records the verdict on the GRN before inwarding any of it, so holding the
 * truck for a verdict buys no quality. What cannot go is what has already been ruled against —
 * failed — or what is out of date. Anything with nothing left has simply gone already.
 *
 * MIRRORS THE SERVER, DELIBERATELY. `assertNoBatchIsBlocked` refuses only BLOCKED batches, and
 * BLOCKED means failed or expired; PENDING resolves to IN_QC and is allowed through. This
 * compared against PASSED instead, which stripped the tick off every pending row and left the
 * register contradicting itself — `dispatchQueue` admitted PENDING and then nothing could pick
 * it. The one rule the two sides must agree on is expressed here in the terms the server uses.
 *
 * Lives here rather than on the register that renders the checkbox, so the rule deciding what
 * may be picked sits beside the rules deciding what may be sent.
 */
export function canShip(row: BatchRegisterRow, today: string = localToday()): boolean {
    // No batch, no verdict and no expiry: whatever is in the bucket may go.
    if (row.noBatch) return row.remaining > 0;
    // AND IN DATE. Expiry outranks the verdict: a batch that passed and then went out of date
    // is exactly what a status-only test waves through. Checked here because the failure would
    // otherwise arrive from the server after the shipment had been built.
    return row.qcStatus !== 'FAILED' && row.remaining > 0 && !isBatchExpired(row, today);
}

/**
 * dd MMM yyyy, or a dash. Shared by the register and the shipment builder, so the date beside
 * a batch code reads the same on both.
 *
 * THE YEAR IS NOT OPTIONAL HERE. Without it "01 May" beside a manufacturing date of "16 Aug"
 * reads as an expiry four months in the PAST, when the goods actually expire in 2029 — the one
 * column where a misread is a batch pulled off a shelf, or left on one.
 */
export function formatDate(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isFinite(date.getTime())
        ? date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : value;
}

/** What a row is called where a batch code would go. */
export function batchLabel(row: { code: string; noBatch?: boolean }): string {
    return row.noBatch ? 'No batch' : row.code;
}

/**
 * The queue's QC split for one row.
 *
 * A batch's split is the common rule; a no-batch row has no verdict to split by — the brand
 * that turned FG tracking off rules on the load at its dock — so everything in the bucket is
 * available and nothing is awaiting QC. Said here once, so the product list and its batch
 * children cannot disagree about it.
 */
export function queueSplit(
    row: BatchRegisterRow,
    today: string,
): { approved: number; pending: number } {
    if (row.noBatch) return { approved: row.remaining, pending: 0 };
    const split = batchQcSplit(row, today);
    return { approved: split.approved, pending: split.pending };
}

/**
 * The dispatch queue: what is still at the site and could yet go.
 *
 * The Batches register deliberately keeps every run, shipped or not, because it is the ledger.
 * This is its complement — a to-do list for whoever owns dispatch — so a row leaves the moment
 * nothing of it remains at the site.
 *
 * PENDING rows are here and pickable: the brand rules on the load when it arrives, so an
 * unruled batch is a batch that may travel. FAILED is excluded because a failed batch is not
 * awaiting dispatch — its only exits are a return or disposal. Expired stock is excluded
 * outright, per the same rule that stops it being picked.
 *
 * FEFO: soonest expiry first, undated batches last, ties broken oldest-made first — the order
 * somebody clearing a shelf should send things in.
 */
export function dispatchQueue(
    rows: BatchRegisterRow[],
    today: string = localToday(),
): BatchRegisterRow[] {
    return rows
        .filter(
            (row) =>
                row.remaining > 0 &&
                (row.noBatch ||
                    ((row.qcStatus === 'PASSED' || row.qcStatus === 'PENDING') &&
                        !isBatchExpired(row, today))),
        )
        .sort((a, b) => {
            if (a.expiryDate !== b.expiryDate) {
                if (!a.expiryDate) return 1;
                if (!b.expiryDate) return -1;
                return a.expiryDate < b.expiryDate ? -1 : 1;
            }
            return (a.manufacturingDate ?? '') < (b.manufacturingDate ?? '') ? -1 : 1;
        });
}

/**
 * Which picker value stands for the order a batch was made against.
 *
 * A person picks the ORDER THEY WERE GIVEN, so a work order belonging to a production order is
 * offered as that order and resolved back per SKU. One raised on its own has nothing to be
 * grouped under and is offered by its own code.
 *
 * READ OFF THE BATCH, not resolved against the site's open orders. It used to be the latter,
 * which quietly returned "no order" for a batch whose order had since shipped or been cancelled
 * - the run was attributed, and the screen said it answered nothing.
 */
export function choiceForBatch(batch: BatchRegisterRow): string {
    if (!batch.workOrderId) return '';
    return batch.productionOrderId
        ? `po:${batch.productionOrderId}`
        : `wo:${batch.workOrderId}`;
}

/**
 * What a row may be sent against: the order it was made for, and stock.
 *
 * NOTHING ELSE, deliberately. The picker used to offer every order open at the site, so a run
 * made for one order could be sent against another by picking the wrong line of a dropdown -
 * and it opened on "send as stock" whatever the batch was made for, because the batch was not
 * carrying its order at all. A batch answers the order it was made against or it answers none.
 *
 * STOCK ALWAYS STAYS. Sending part of a run to the order and the remainder as stock is an
 * ordinary shipment, and `splitRow` exists to build exactly that.
 */
export function orderOptionsForRow(
    row: ShipmentRow,
    skuOrderOptions: Array<{ value: string; label: string }> = [],
    stockLabel = 'No order — send as stock',
): Array<{ value: string; label: string }> {
    const stock = { value: '', label: stockLabel };

    // A batch made FOR an order answers that order or none - offering the rest of the book
    // here is how a run got attributed to the wrong order once.
    if (row.hasProvenance && row.ownChoice) {
        return [stock, { value: row.ownChoice, label: row.ownLabel ?? row.ownChoice }];
    }

    /*
     * A FREE BATCH answers whichever open order asks for its SKU - which is exactly what a
     * produce-to-stock site does all day, and what this picker used to forbid: the only offer
     * was "send as stock", so shelf stock could never fill the order it was covering.
     */
    const options = [stock, ...skuOrderOptions];

    if (row.ownChoice && !options.some((option) => option.value === row.ownChoice)) {
        options.push({ value: row.ownChoice, label: row.ownLabel ?? row.ownChoice });
    }

    return options;
}

/**
 * The picked batches, ready to edit — one line each to begin with.
 *
 * THE WHOLE BATCH BY DEFAULT. Somebody who ticked a run means to send it; sending part of one is
 * the exception, and starting from empty would make the common case the one that needs typing.
 *
 * THE ORDER IS A DEFAULT, NOT A CONCLUSION. It comes from the batch's own work-order link, which
 * is provenance — which order triggered the run — and is right most of the time and empty rather
 * often: on a real site the batches with stock on hand are frequently produce-to-stock runs
 * carrying no link at all, which can still answer an order. So it is pre-filled where there is
 * something to pre-fill and left for a person to say where there is not.
 */
export function buildShipmentRows(batches: BatchRegisterRow[]): ShipmentRow[] {
    return batches.map((batch) => {
        const provenance = choiceForBatch(batch);
        // FIFO's pick for a free batch, stamped by the server off the same allocation the
        // queue displays - so the default here and the cover shown there cannot disagree.
        const suggestion = batch.suggestedProductionOrderId
            ? `po:${batch.suggestedProductionOrderId}`
            : batch.suggestedWorkOrderId
              ? `wo:${batch.suggestedWorkOrderId}`
              : '';
        const ownChoice = provenance || suggestion;
        return {
            lineId: `${batch.batchId}::0`,
            batchId: batch.batchId,
            code: batch.code,
            noBatch: batch.noBatch,
            itemId: batch.itemId ?? '',
            itemName: batch.itemName ?? batch.itemId ?? '',
            available: batch.remaining,
            quantity: String(batch.remaining),
            orderChoice: ownChoice,
            ownChoice,
            ownLabel: provenance
                ? (batch.productionOrderCode ?? batch.workOrderCode ?? null)
                : (batch.suggestedOrderCode ?? null),
            hasProvenance: Boolean(provenance),
            expiryDate: batch.expiryDate ?? null,
        };
    });
}

/**
 * A second line drawing on the same batch, for what the first one cannot carry.
 *
 * THIS IS THE WAY OUT OF THE CAP. A 400-unit run answering an order that wants 350 has to send
 * 350 there and 50 as stock. Without it, the only way past a refusal was to drop the order from
 * the row — and since one bad line blocks the whole shipment, the cheapest fix was to drop it
 * from every row, which is how a consignment ends up attributed to nothing.
 *
 * SEEDED WITH WHAT IS LEFT OVER, and with no order. The reason to split is almost always that
 * the order could not take the whole batch, so the remainder is the interesting number and where
 * it goes is the open question.
 */
export function splitRow(rows: ShipmentRow[], lineId: string, seq: number): ShipmentRow[] {
    const index = rows.findIndex((row) => row.lineId === lineId);
    if (index === -1) return rows;

    const source = rows[index];
    const alreadyOnBatch = rows
        .filter((row) => row.batchId === source.batchId)
        .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);

    const next: ShipmentRow = {
        ...source,
        // A counter rather than a clock: unique for as long as the modal is open, which is all
        // the identity a row needs, and reproducible in a test.
        lineId: `${source.batchId}::${seq}`,
        quantity: String(Math.max(0, source.available - alreadyOnBatch)),
        orderChoice: '',
    };

    return [...rows.slice(0, index + 1), next, ...rows.slice(index + 1)];
}

/** How much of each order is still unfilled, from what the site's own job list already says. */
export function outstandingByWorkOrder(orders: SiteOrder[]): Map<string, number> {
    const outstanding = new Map<string, number>();
    for (const order of orders) {
        for (const line of order.lines ?? []) {
            outstanding.set(line.workOrderId, Math.max(0, line.ordered - line.dispatched));
        }
    }
    return outstanding;
}

export interface RowProblem {
    /** Blocks the shipment. A warning that did not block would be decoration. */
    quantity?: string;
    order?: string;
}

/** The work order a row lands on, or null for stock. */
function workOrderFor(row: ShipmentRow, orderLines: OrderLine[]): string | null {
    return resolveWorkOrder(row.orderChoice, row.itemId, orderLines).workOrderId;
}

/** What a row is actually sending, with a half-typed quantity counting for nothing. */
function quantityOf(row: ShipmentRow): number {
    const quantity = Number(row.quantity);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
}

/**
 * Everything wrong with the consignment, said against the rows that caused it.
 *
 * THE TOTALS ARE WHAT MATTER, and both of them span rows:
 *
 * A BATCH is checked as a whole, because all its lines draw on one shelf. Two lines of 300
 * against a batch of 400 each look fine alone and overdraw it together — the same trap, one
 * level down, that the per-order sum exists to close.
 *
 * AN ORDER is checked as a whole, for the same reason.
 *
 * EVERY ROW IN A FAILING GROUP IS MARKED. Naming only the group left "which of these five do I
 * change" unanswered on screen, and the cheapest way out of that was to strip the order off all
 * of them — which is exactly what went wrong on the first real shipment.
 */
export function checkShipment(
    rows: ShipmentRow[],
    orderLines: OrderLine[],
    outstanding: Map<string, number>,
): { rowProblems: Map<string, RowProblem>; overfilled: string[] } {
    const rowProblems = new Map<string, RowProblem>();
    const mark = (lineId: string, problem: RowProblem) =>
        rowProblems.set(lineId, { ...rowProblems.get(lineId), ...problem });

    for (const row of rows) {
        const quantity = Number(row.quantity);
        if (!row.quantity.trim() || !Number.isFinite(quantity) || quantity <= 0) {
            mark(row.lineId, { quantity: 'Say how much is going.' });
        }
        if (resolveWorkOrder(row.orderChoice, row.itemId, orderLines).unmatched) {
            // Picking an order that never asked for this SKU is a mis-click, not a shipment.
            mark(row.lineId, { order: 'That order does not include this item.' });
        }
    }

    const perBatch = new Map<string, ShipmentRow[]>();
    for (const row of rows) {
        perBatch.set(row.batchId, [...(perBatch.get(row.batchId) ?? []), row]);
    }
    for (const batchRows of perBatch.values()) {
        const total = batchRows.reduce((sum, row) => sum + quantityOf(row), 0);
        const available = batchRows[0].available;
        if (total > available) {
            for (const row of batchRows) {
                mark(row.lineId, {
                    quantity:
                        batchRows.length > 1
                            ? `These lines want ${total} of a batch holding ${available}.`
                            : `Only ${available} left.`,
                });
            }
        }
    }

    const perOrder = new Map<string, ShipmentRow[]>();
    for (const row of rows) {
        const workOrderId = workOrderFor(row, orderLines);
        if (!workOrderId) continue;
        perOrder.set(workOrderId, [...(perOrder.get(workOrderId) ?? []), row]);
    }

    const overfilled: string[] = [];
    for (const [workOrderId, orderRows] of perOrder) {
        const left = outstanding.get(workOrderId);
        if (left === undefined) continue;
        const total = orderRows.reduce((sum, row) => sum + quantityOf(row), 0);
        if (total > left) {
            const code =
                orderLines.find((line) => line.workOrderId === workOrderId)?.code ?? workOrderId;
            // NAMES THE WAY OUT, not just the refusal. Splitting the remainder to stock is the
            // fix; leaving somebody to find that themselves is what cost the attribution before.
            overfilled.push(
                `${code} has ${left} left to fill, and ${total} is going against it. Send ${left} against it and split the rest to stock.`,
            );
            for (const row of orderRows) {
                mark(row.lineId, { order: `Over by ${total - left}` });
            }
        }
    }

    return { rowProblems, overfilled };
}

/**
 * The rows as the server wants them: one line per draw, each naming its own order.
 *
 * A no-batch row names its PRODUCT and no batch — its `batchId` is the register's own key
 * for selecting it, and sending that as a batch would be refused as not being one.
 */
export function toDispatchLines(rows: ShipmentRow[], orderLines: OrderLine[]): DispatchLine[] {
    return rows.map((row) => ({
        ...(row.noBatch ? { productId: row.itemId } : { batchId: row.batchId }),
        quantity: Number(row.quantity),
        workOrderId: workOrderFor(row, orderLines) ?? undefined,
    }));
}

/**
 * "3 batches · 650 units", for a bar that has to say what is about to happen in one line.
 *
 * BATCHES ARE COUNTED DISTINCTLY, so a batch split across two lines is still one batch — what a
 * person ticked, rather than how many rows it took to describe.
 *
 * Takes only what it needs, so the selection bar can call it on picked register rows before a
 * builder row exists for them and get wording identical to the modal's own title.
 */
export function summarise(
    rows: Array<{ quantity: string; batchId?: string; noBatch?: boolean }>,
): string {
    const units = rows.reduce((sum, row) => {
        const quantity = Number(row.quantity);
        return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
    }, 0);
    const count = rows[0]?.batchId ? new Set(rows.map((row) => row.batchId)).size : rows.length;
    // "Lines" once anything here has no batch: "2 batches" would be claiming a batch exists.
    const noun = rows.some((row) => row.noBatch)
        ? `line${count === 1 ? '' : 's'}`
        : `batch${count === 1 ? '' : 'es'}`;
    const batches = `${count} ${noun}`;
    return `${batches} · ${formatCount(units)} unit${units === 1 ? '' : 's'}`;
}

// ─── Dispatching by product ──────────────────────────────────────────────────

/** One product's dispatchable batches, and what they add up to. */
export interface ProductGroup {
    itemId: string;
    itemName: string;
    /** FEFO order, inherited from the queue this was grouped out of. */
    batches: BatchRegisterRow[];
    /** What the batches hold between them. */
    available: number;
    /** Of that, what QC has cleared and what it has not yet ruled on. */
    approved: number;
    pending: number;
    /** The first date anything here goes off, for the urgency the batch view shows per row. */
    earliestExpiry: string | null;
}

/**
 * The dispatch queue as products rather than batches.
 *
 * WHY IT IS A VIEW AND NOT A FETCH: every figure here is already on the page. The queue is
 * `dispatchQueue`'s output, which is the register filtered to what can still go and sorted
 * FEFO, so grouping is arithmetic rather than a second read of the same shelf.
 *
 * ORDER IS THE QUEUE'S ORDER. Products appear in the order their most urgent batch does, so the
 * top of the product list is the thing to send first — the same claim the batch list makes, at
 * the grain somebody is actually asked to ship in.
 *
 * BATCHES KEEP THEIR FEFO ORDER INSIDE THE GROUP, which is what lets the allocator walk them
 * without sorting again, and what makes "oldest first" true of the rows on screen.
 */
export function groupQueueByProduct(queue: BatchRegisterRow[]): ProductGroup[] {
    const order: string[] = [];
    const byItem = new Map<string, ProductGroup>();

    for (const batch of queue) {
        const itemId = batch.itemId ?? '';
        if (!itemId) continue;

        let group = byItem.get(itemId);
        if (!group) {
            group = {
                itemId,
                itemName: batch.itemName ?? itemId,
                batches: [],
                available: 0,
                approved: 0,
                pending: 0,
                earliestExpiry: null,
            };
            byItem.set(itemId, group);
            order.push(itemId);
        }

        group.batches.push(batch);
        group.available += batch.remaining;

        // The same split the register shows per batch, summed. Rejected and expired are
        // already out - the queue excluded them - so only these two can be non-zero here.
        const split = queueSplit(batch, localToday());
        group.approved += split.approved;
        group.pending += split.pending;

        if (
            batch.expiryDate &&
            (!group.earliestExpiry || batch.expiryDate < group.earliestExpiry)
        ) {
            group.earliestExpiry = batch.expiryDate;
        }
    }

    return order.map((itemId) => byItem.get(itemId)!);
}

/**
 * A line drawing on a batch while a batch of the same product that should leave first still
 * has stock - "RUN-0208 expires sooner and still has 96", keyed by the line to say it under.
 *
 * THE ORDER IS THE ROWS' ORDER. The builder lists batches the way the dispatch queue does -
 * soonest expiry first, undated last, oldest made first on ties - so "should leave first" is
 * simply "appears earlier for the same product", and this needs no second sort to disagree
 * with the one on screen.
 *
 * PER BATCH, NOT PER LINE. A batch split across two orders is one shelf; what it still has is
 * what its lines leave between them.
 *
 * A WARNING, NOT A BLOCK, and only the first skipped batch is named. Floor discipline is
 * surfaced rather than enforced everywhere else in the portal, and one sentence naming the
 * batch to go and fill is more use than a list. The no-batch bucket has no date to be sooner
 * by, so it neither nudges nor is nudged about.
 */
export function fefoNudges(rows: ShipmentRow[]): Map<string, string> {
    const nudges = new Map<string, string>();

    const sentByBatch = new Map<string, number>();
    for (const row of rows) {
        sentByBatch.set(row.batchId, (sentByBatch.get(row.batchId) ?? 0) + quantityOf(row));
    }
    const stillHas = (row: ShipmentRow) => row.available - (sentByBatch.get(row.batchId) ?? 0);

    // The first line of every batch met so far, per product, in the order they appeared.
    const earlierByItem = new Map<string, ShipmentRow[]>();
    for (const row of rows) {
        if (row.noBatch) continue;
        const earlier = earlierByItem.get(row.itemId) ?? [];

        if (quantityOf(row) > 0) {
            const skipped = earlier.find(
                (prior) => prior.batchId !== row.batchId && stillHas(prior) > 0,
            );
            if (skipped) {
                const sooner = skipped.expiryDate ? 'expires sooner' : 'is older';
                nudges.set(
                    row.lineId,
                    `${batchLabel(skipped)} ${sooner} and still has ${formatCount(stillHas(skipped))}`,
                );
            }
        }

        if (!earlier.some((prior) => prior.batchId === row.batchId)) {
            earlierByItem.set(row.itemId, [...earlier, row]);
        }
    }

    return nudges;
}
